import React, { useMemo } from 'react';
import {
  Box, VStack, HStack, Text, Spinner, Center, ScrollView, Pressable, Icon, Button, ButtonText, ButtonIcon,
} from '@gluestack-ui/themed';
import {
  X, Mail, FileText, AlertCircle,
} from 'lucide-react-native';
import { Linking } from 'react-native';
import { useGetTasksByUserCourseModule } from '@/common/api/endpoints/tasks/tasks';
import { useGetUser } from '@/common/api/endpoints/users/users';
import AttachmentMenu from '@/student/task/components/Attachments/AttachmentMenu';
import AttachmentItem from '@/student/task/components/Attachments/AttachmentItem';
import type { Attachment } from '@/common/api/models/attachment';
import {
  extractDimensionComments,
  getFlatEvaluationDimensions,
} from '@/common/utils/evaluation/getFlatEvaluationDimensions';
import HighlightedSubmissionText from '../../components/SubmissionElement/HighlightedSubmissionText';
import TaskContextPreview from '../../components/SubmissionElement/components/TaskContextPreview';
import { useEngagementStore } from '../EngagementStore';
import type { DimensionCriterion } from '../../components/SubmissionElement/components/types';

const StudentWorkDrawer: React.FC = () => {
  const {
    selectedTaskId,
    selectedStudentId,
    selectedCourseId,
    activeModuleId,
    activeModuleTitle,
    closeWorkspace,
  } = useEngagementStore();

  const isOpen = !!(selectedTaskId && selectedStudentId);

  // Fetch the specific task (via list for now, as singular endpoint might be tricky with includes)
  const { data: tasksData, isLoading } = useGetTasksByUserCourseModule(
    selectedStudentId || '',
    selectedCourseId || '',
    activeModuleId || '',
    { include: 'submissions' },
    {
      query: {
        enabled: isOpen && !!selectedStudentId && !!selectedCourseId && !!activeModuleId,
        staleTime: 1000 * 60, // 1 minute
      },
    },
  );

  const { data: userData } = useGetUser(
    selectedStudentId || '',
    {
      query: {
        enabled: isOpen && !!selectedStudentId,
        staleTime: 1000 * 60 * 5, // 5 minutes
      },
    },
  );

  const student = userData?.data?.data;

  const isQueryLoading = isLoading;

  const task = useMemo(() => {
    if (!tasksData?.data?.data) return null;
    return tasksData.data.data.find((t) => t.id === selectedTaskId);
  }, [tasksData, selectedTaskId]);

  const handleClose = () => {
    closeWorkspace();
  };

  const handleSendFeedback = () => {
    if (!task || !selectedStudentId) return;

    const email = student?.email || '';
    const subject = `Feedback: ${activeModuleTitle ? `${activeModuleTitle} - ` : ''}${task.title}`;
    const body = `Hi,\n\nI was reviewing your draft for "${task.title}" in the module "${activeModuleTitle || 'your recent module'}" and wanted to share some feedback:\n\n`;
    const url = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    Linking.openURL(url);
  };

  // Prepare props for TaskContextPreview
  const autogradeReasoning = useMemo(
    () => extractDimensionComments(getFlatEvaluationDimensions(task?.submission?.evaluation)),
    [task],
  );

  // Parse rubric criteria (simplified from SubmissionElement)
  const dimensionCriteria = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rubric = task?.rubric as Record<string, any> | undefined;
    if (!rubric || !Array.isArray(rubric.dimensions)) return {};
    const criteria: Record<string, DimensionCriterion[]> = {};

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rubric.dimensions.forEach((rubricDim: any) => {
      if (rubricDim?.name && Array.isArray(rubricDim?.criteria)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        criteria[rubricDim.name.toLowerCase()] = rubricDim.criteria.map((criterion: any) => ({
          name: criterion.name,
          descriptor: criterion.descriptor,
          pts: criterion.pts,
          critical: criterion.dimension_critical || false,
        }));
      }
    });
    return criteria;
  }, [task?.rubric]);

  // Highlight config
  const highlightConfig = task?.submission?.feedback?.highlighting || null;

  if (!isOpen) return null;

  return (
    <>
      <Pressable
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        w="100%"
        h="100%"
        bg="$backgroundDark900"
        opacity={0.3}
        zIndex={99}
        onPress={handleClose}
      />
      <Box
        position="absolute"
        right={0}
        top={0}
        bottom={0}
        width={600}
        bg="$white"
        borderLeftWidth={1}
        borderLeftColor="$borderLight200"
        softShadow="3"
        zIndex={100}
      >
        {/* Header */}
        <HStack
          px="$6"
          py="$4"
          borderBottomWidth={1}
          borderBottomColor="$borderLight100"
          alignItems="center"
          justifyContent="space-between"
          bg="$backgroundLight0"
        >
          <VStack flex={1} mr="$4">
            <Text size="xs" color="$textSubtle" textTransform="uppercase" fontWeight="bold">
              Inspecting Student Work
            </Text>
            <Text size="md" color="$textHeading" fontWeight="semibold" numberOfLines={1}>
              {task?.title || 'Loading task...'}
            </Text>
          </VStack>
          <Pressable onPress={handleClose} p="$2" $hover={{ bg: '$bgHover', borderRadius: '$full' }}>
            <Icon as={X} size="md" color="$textSubtle" />
          </Pressable>
        </HStack>

        {/* Content */}
        {isQueryLoading ? (
          <Center flex={1}>
            <Spinner size="large" />
          </Center>
        ) : !selectedCourseId ? (
          <Center flex={1} p="$8">
            <Icon as={AlertCircle} size="xl" color="$warning500" mb="$4" />
            <Text color="$textSubtle" textAlign="center">
              Missing course context. This is likely due to stale cached data. Please hard-refresh the page to view this task.
            </Text>
          </Center>
        ) : task ? (
          <VStack flex={1}>
            <ScrollView flex={1} contentContainerStyle={{ padding: 24, paddingBottom: 100 }}>
              <VStack space="2xl">

                {/* Context Section (Objectives etc) */}
                <Box>
                  <Text size="sm" fontWeight="bold" color="$textSubtle" mb="$2">
                    TASK CONTEXT
                  </Text>
                  <Box
                    bg="$backgroundLight50"
                    p="$4"
                    borderRadius="$lg"
                    borderWidth={1}
                    borderColor="$borderLight100"
                  >
                    <TaskContextPreview
                      task={task}
                      autogradeData={autogradeReasoning}
                      showAutogradeReasoning={!!autogradeReasoning}
                      dimensionCriteria={dimensionCriteria}
                      disableFade
                    />
                  </Box>
                </Box>

                {/* Student Work Section */}
                <Box>
                  <Text size="sm" fontWeight="bold" color="$textSubtle" mb="$2">
                    STUDENT DRAFT
                  </Text>

                  {/* Text Content */}
                  {task.submission?.text ? (
                    <Box
                      bg="$white"
                      p="$6"
                      borderRadius="$lg"
                      borderWidth={1}
                      borderColor="$borderLight200"
                      minHeight={200}
                    >
                      <HighlightedSubmissionText
                        text={task.submission.text}
                        highlightConfig={highlightConfig}
                      />
                    </Box>
                  ) : (
                    <Center bg="$backgroundLight50" p="$8" borderRadius="$lg" borderWidth={1} borderColor="$borderLight100" borderStyle="dashed">
                      <Icon as={FileText} size="xl" color="$textHint" mb="$2" />
                      <Text color="$textHint">No text content drafted yet.</Text>
                    </Center>
                  )}

                  {/* Attachments */}
                  {task.submission?.attachments && task.submission.attachments.length > 0 && (
                  <Box mt="$4">
                    <Text size="xs" fontWeight="semibold" color="$textSubtle" mb="$2">
                      ATTACHMENTS (
                      {task.submission.attachments.length}
                      )
                    </Text>
                    <HStack space="md" flexWrap="wrap">
                      {task.submission.attachments.map((attachment: Attachment) => (
                        <AttachmentMenu
                          key={attachment.id}
                          attachment={attachment}
                          allowDelete={false}
                          onDeleteAttachment={() => {}}
                          isDeleting={false}
                          trigger={(triggerProps: Record<string, unknown>) => (
                            <AttachmentItem
                              attachment={attachment}
                              isDeleting={false}
                              triggerProps={triggerProps}
                            />
                          )}
                        />
                      ))}
                    </HStack>
                  </Box>
                  )}
                </Box>

              </VStack>
            </ScrollView>

            {/* Footer Actions */}
            <Box
              p="$4"
              borderTopWidth={1}
              borderTopColor="$borderLight200"
              bg="$white"
            >
              <Button size="lg" action="primary" onPress={handleSendFeedback}>
                <ButtonIcon as={Mail} mr="$2" />
                <ButtonText>Send Feedback via Email</ButtonText>
              </Button>
            </Box>
          </VStack>
        ) : (
          <Center flex={1}>
            <Text color="$textSubtle">Select a task to view details.</Text>
          </Center>
        )}
      </Box>
    </>
  );
};

export default StudentWorkDrawer;
